import { useEffect, useState } from 'react';
import { Hub } from 'aws-amplify/utils';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import { setConfigThunk } from '../app/configSlice';
import { signOutSession } from '../utils/auth/signOutSession';
import { hydrateCognitoAuthUser } from '../utils/auth/hydrateCognitoAuthUser';

import { resetUserData, selectAuthUser } from '../app/authSlice';
import { userApi } from '../api/userApi';
import { kitchenApi } from '../api/kitchenApi';
import { LoginType } from '../api-types/models/LoginDto';
import { useIsAuthenticated } from '@azure/msal-react';

const useAmplifyAuth = () => {
    const [triggerFetch, setTriggerFetch] = useState(false);
    const dispatch = useAppDispatch();
    const isAuthenticated = useIsAuthenticated();
    const authUser = useAppSelector(selectAuthUser);

    useEffect(() => {
        let isMounted = true;
        const loginType = localStorage.getItem('login');

        const fetchUserData = async () => {
            if (!isMounted) {
                return;
            }

            // SSO users authenticate via Azure AD (MSAL), not Cognito.
            // Skip the Cognito session lookup to avoid stale sessions
            // overwriting route access or triggering a logout.
            if (loginType === LoginType.SPORTRADAR) {
                if (isAuthenticated) {
                    dispatch(
                        userApi.util.prefetch('getUserSettings', undefined, {
                            force: true
                        })
                    );
                }
                return;
            }

            if (!loginType || authUser) {
                return;
            }

            if (isMounted) {
                await hydrateCognitoAuthUser(dispatch);
            }
        };

        const onAuthEvent = (data: { payload: { event: string } }) => {
            switch (data.payload.event) {
                case 'signedIn':
                    if (isMounted) {
                        setTriggerFetch(true);
                        console.log('signed in');
                    }
                    break;
                default:
                    return;
            }
        };

        const hubUnsubscribe = Hub.listen('auth', (data) => {
            onAuthEvent(data);
        });

        fetchUserData().catch((error) => {
            console.log('Error fetching user data ', error);
        });

        return () => {
            isMounted = false;
            hubUnsubscribe();
        };
    }, [dispatch, triggerFetch, isAuthenticated, authUser]);

    const handleSignout = async () => {
        try {
            console.log('signed out');
            setTriggerFetch(false);
            dispatch(resetUserData());
            dispatch(kitchenApi.util.resetApiState());
            await signOutSession();
            await dispatch(setConfigThunk(undefined));
        } catch (error) {
            console.error('Error signing out user ', error);
        }
    };

    return { handleSignout };
};

export default useAmplifyAuth;
